/**
 * Bottom-right keybind hint list ("Next target  [Tab]"): small, right-aligned, outlined text.
 * H collapses it to a single "Keybinds [H]" row (remembered per browser). Release only shows
 * while you are dead.
 */
import type { HudUpdate } from '../hud';
import { el, setClass } from '../dom';
import type { HudWidget } from '../widgets';
import { commandKeyLabel } from './hotkeys';
import { controlSettings } from '../../input/controlSettings'; // +controls
import type { ControlScheme } from '../../input/controls';

const KEY = 'keystone.hudHints.v1';

export interface KeyHint {
  label: string;
  key: string;
  /** Only shown while the player is dead. */
  deadOnly?: boolean;
}

/** +controls: movement rows per control scheme (the top of the list). */
export const MOVE_HINTS: Readonly<Record<ControlScheme, readonly KeyHint[]>> = {
  action: [
    { label: 'Strafe', key: 'A / D' },
    { label: 'Turn', key: 'Right-drag' },
  ],
  wow: [
    { label: 'Turn', key: 'A / D' },
    { label: 'Steer · strafe', key: 'Right-drag' },
  ],
  camera: [{ label: 'Look', key: 'Drag' }],
};

export const KEY_HINTS: readonly KeyHint[] = [
  { label: 'Next target', key: 'Tab' },
  { label: 'Jump', key: 'Space' }, // +jump
  { label: 'Glide', key: 'Hold Space' }, // +glide: held in the air
  { label: 'Dodge', key: 'Shift' }, // +jump: Dodge moved off Space
  { label: 'Auto', key: 'Z' }, // +field
  { label: 'Cancel · clear target', key: 'Esc' },
  { label: 'Release spirit', key: commandKeyLabel('release'), deadOnly: true },
  { label: 'Pause', key: 'P' },
  { label: 'Hide interface', key: commandKeyLabel('toggleUi') },
  { label: 'Hide keybinds', key: commandKeyLabel('hints') },
];

function load(): boolean {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem(KEY) === 'hidden';
  } catch {
    return false;
  }
}

export class KeyHints implements HudWidget {
  readonly root: HTMLElement;
  private readonly deadRows: HTMLElement[] = [];
  private hidden = load();
  private dead = false;
  private readonly moveRows: HTMLElement;
  private scheme: ControlScheme | null = null;

  constructor(host: HTMLElement) {
    const root = (this.root = el('div', 'hk-hints', host));
    root.setAttribute('aria-label', 'Keybinds');
    this.moveRows = el('div', 'hk-hints-move', root); // +controls
    this.syncMoveRows();
    for (const h of KEY_HINTS) {
      const row = el('div', 'hk-hint', root);
      el('span', '', row, h.label);
      el('span', 'hk-keycap', row, h.key);
      if (h.deadOnly) {
        row.classList.add('dead-only', 'hk-hidden');
        this.deadRows.push(row);
      }
    }
    const stub = el('div', 'hk-hint hk-hints-stub', root);
    el('span', '', stub, 'Keybinds');
    el('span', 'hk-keycap', stub, commandKeyLabel('hints'));
    this.apply();
  }

  toggle(force?: boolean): void {
    this.hidden = force ?? !this.hidden;
    try {
      localStorage.setItem(KEY, this.hidden ? 'hidden' : 'shown');
    } catch {
      /* not remembered */
    }
    this.apply();
  }

  private apply(): void {
    setClass(this.root, 'collapsed', this.hidden);
  }

  /** +controls: the movement rows follow the Controls setting. */
  private syncMoveRows(): void {
    const scheme = controlSettings.get().scheme;
    if (scheme === this.scheme) return;
    this.scheme = scheme;
    this.moveRows.replaceChildren();
    for (const h of MOVE_HINTS[scheme]) {
      const row = el('div', 'hk-hint', this.moveRows);
      el('span', '', row, h.label);
      el('span', 'hk-keycap', row, h.key);
    }
  }

  update(h: HudUpdate): void {
    this.syncMoveRows();
    const me = h.playerId ? h.world.units[h.playerId] : undefined;
    const dead = !!me && !me.alive;
    if (dead === this.dead) return;
    this.dead = dead;
    for (const r of this.deadRows) setClass(r, 'hk-hidden', !dead);
  }
}

export const keyHintsRef: { current: KeyHints | null } = { current: null };
